/**
 * Admin Panel Component
 *
 * Main admin interface for managing project tracker taxonomies:
 * - Therapeutic Areas (MedDRA-based)
 * - Indications (NCIt-based)
 * - Team Roles
 *
 * Only accessible to platform admins (sec_pp-admin group).
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Science as ScienceIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useReferenceData } from '../../hooks/useReferenceData';
import { LoadingSpinner } from '../../../../components/loading/LoadingStates';
import { ErrorDisplay } from '../../../../components/errors/ErrorDisplay';
import TherapeuticAreaManager from './TherapeuticAreaManager';
import IndicationManager from './IndicationManager';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const { referenceData, loading, error, loadReferenceData, updateReferenceData } =
    useReferenceData();

  const [currentTab, setCurrentTab] = useState(0);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setUpdateError(null);
    setUpdateSuccess(null);
  };

  const handleUpdateTherapeuticAreas = async (
    therapeuticAreas: NonNullable<typeof referenceData>['therapeuticAreas']
  ) => {
    if (!referenceData) return;

    setUpdateError(null);
    setUpdateSuccess(null);

    try {
      await updateReferenceData({
        ...referenceData,
        therapeuticAreas,
      });
      setUpdateSuccess('Therapeutic areas updated successfully');
      setTimeout(() => setUpdateSuccess(null), 3000);
    } catch (err: any) {
      setUpdateError(err.message || 'Failed to update therapeutic areas');
      throw err;
    }
  };

  const handleUpdateIndications = async (
    indications: NonNullable<typeof referenceData>['indications']
  ) => {
    if (!referenceData) return;

    setUpdateError(null);
    setUpdateSuccess(null);

    try {
      await updateReferenceData({
        ...referenceData,
        indications,
      });
      setUpdateSuccess('Indications updated successfully');
      setTimeout(() => setUpdateSuccess(null), 3000);
    } catch (err: any) {
      setUpdateError(err.message || 'Failed to update indications');
      throw err;
    }
  };


  if (loading && !referenceData) {
    return <LoadingSpinner message="Loading taxonomy data..." />;
  }

  if (error || !referenceData) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorDisplay
          title="Failed to load reference data"
          error={error || 'Reference data not found'}
          onRetry={loadReferenceData}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/projects')} sx={{ mr: 2, color: 'white' }}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ color: 'white' }}>Taxonomy Management</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Manage controlled vocabularies for project classification
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadReferenceData} disabled={loading} sx={{ color: 'white' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Alerts */}
      {updateError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUpdateError(null)}>
          {updateError}
        </Alert>
      )}
      {updateSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUpdateSuccess(null)}>
          {updateSuccess}
        </Alert>
      )}

      {/* Info Banner */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Therapeutic Areas</strong> and <strong>Indications</strong> are controlled
          vocabularies based on MedDRA and NCIt standards for consistent project classification.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Free text fields</strong> (not managed here): Clients, Products, Activities,
          Sub-activities, and <strong>Team Roles</strong>. Users enter these freely for maximum
          flexibility.
        </Typography>
      </Alert>

      {/* Tabs */}
      <Paper>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab icon={<ScienceIcon />} label="Therapeutic Areas" iconPosition="start" />
          <Tab icon={<HospitalIcon />} label="Indications" iconPosition="start" />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <TherapeuticAreaManager
            therapeuticAreas={referenceData.therapeuticAreas}
            onUpdate={handleUpdateTherapeuticAreas}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <IndicationManager
            indications={referenceData.indications}
            therapeuticAreas={referenceData.therapeuticAreas}
            onUpdate={handleUpdateIndications}
          />
        </TabPanel>
      </Paper>

      {/* Footer Info */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Last updated: {new Date(referenceData.updatedAt).toLocaleString()} • Version:{' '}
          {referenceData.version}
        </Typography>
      </Box>
    </Box>
  );
};

export default AdminPanel;
